import { Form, Formik, validateYupSchema, yupToFormErrors } from 'formik'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import Breadcrumbs from '../../components/atomic/Breadcrumbs'
import MetaSeo from '../../components/atomic/MetaSeo'
import Trans from '../../components/atomic/Trans'
import Step1 from '../../components/registrazione/Step1'
import {
  altro,
  schemaStep1,
  schemaStep2,
  schemaStep2Estero,
} from '../../components/registrazione/utils'

import Link from 'next/link'
import { api, RECAPTCHA_KEY } from '../../utils'
import { PAGINEDETAIL, REGISTRAZIONE } from '../../utils/endpoint'
import { sendGTMData } from '../../utils/gtm'

const Login = dynamic(() => import('../../components/atomic/Login'))
const Modal = dynamic(() => import('../../components/atomic/Modal'))
const Step2 = dynamic(() => import('../../components/registrazione/Step2'))
const Vantaggi = dynamic(() => import('../../components/registrazione/Vantaggi'))

const REGISTRATION_DISABLED = false

api.defaults.xsrfCookieName = 'csrftoken'
api.defaults.xsrfHeaderName = 'X-CSRFTOKEN'

const Registrazione = ({ pagina }) => {
  const [step, setStep] = useState(1)
  const [emailExists, setEmailExists] = useState(false)
  const [isRefresh, setIsRefresh] = useState(false)
  const [hasFlag, setHasFlag] = useState(false)
  const [fieldsStep1, setFieldsStep1] = useState({
    nome: '',
    cognome: '',
    email: '',
    confermaEmail: '',
    password: '',
    confermaPassword: '',
  })
  const [login, setLogin] = useState(false)

  const handleLogin = (login) => setLogin(login)

  const router = useRouter()
  const query = router.query

  let recaptchaRef = useRef(null)

  // se utente già registrato o migrato da vecchio sito => refresh=true
  const registraUtente = async (values) => {
    const token =
      process.env.NODE_ENV == 'production' ? await recaptchaRef.current.executeAsync() : 'token' //reCAPTCHA

    if (token) {
      api
        .post(REGISTRAZIONE + 'post_data/', { ...values, refresh: isRefresh, hasFlag: hasFlag })
        .then((response) => {
          sendGTMData({
            event: 'GAevent',
            eventID: '09',
            eventCategory: 'registrazione',
            eventAction: 'email',
            eventLabel: 'step2',
          })

          if (query?.next) {
            router.push(query?.next)
          } else {
            router.push(`/registrazione/thank-you?logged=${response.data.user_is_logged}`)
          }
        })
        .catch((error) => {
          console.log(error)
        })
    }
  }

  const goToStep2 = (formik) => {
    const params = { email: formik.values.email }

    api
      .get(REGISTRAZIONE + 'check_email/', { params })
      .then((response) => {
        sendGTMData({
          event: 'GAevent',
          eventID: '09',
          eventCategory: 'registrazione',
          eventAction: 'email',
          eventLabel: 'step1',
        })
        if (response.data.exists && !isRefresh) setEmailExists(true)
        else {
          formik.setSubmitting(false)
          setStep(2)
        }
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const getClassFromStep = (number) => {
    return number === step ? 'steps__item--full' : 'steps__item--disabled'
  }

  const getSchema = (values) => {
    if (step === 1) return schemaStep1
    else {
      if (values.scuole[0].regione && values.scuole[0].regione.value === altro) {
        return schemaStep2Estero
      } else {
        return schemaStep2
      }
    }
  }

  const loadInitialData = () => {
    const params = { user_uid: query.user_uid }

    api
      .get(REGISTRAZIONE + 'load_initial/', { params })
      .then((response) => {
        const data = {
          ...fieldsStep1,
          nome: response.data.nome,
          cognome: response.data.cognome,
          email: response.data.email,
          confermaEmail: response.data.email,
          password: 'giuntiscuola',
          confermaPassword: 'giuntiscuola',
        }

        setFieldsStep1(data)

        setHasFlag(response.data.has_flag)

        if (response.data.nome && response.data.cognome && response.data.email) setStep(2) // senza nome e cognome rimango in step 1
      })
      .catch((error) => {
        console.log(error)
      })
  }

  useEffect(() => {
    if (query?.user_uid !== undefined) {
      setIsRefresh(true)
      loadInitialData()
    }
  }, [query?.user_uid])

  const fieldsStep2 = {
    scuole: [{ provincia: '', citta: '', scuola: '' }],
    privacy: hasFlag,
    commerciale: false,
    profilazione: false,
    isEstero: false,
    provincia: { value: '' },
    telefono: '',
  }

  return (
    <>
      <MetaSeo seo={pagina.seo} />
      <main className="registrazione-page">
        <header className="registrazione-page__header">
          <div className="w-container">
            <Breadcrumbs breadcrumbs={pagina.breadcrumbs} />
            <h1 className="registrazione-page__title">
              {isRefresh ? (
                <span key={isRefresh}>
                  <Trans chiave="Completa la registrazione" />
                </span>
              ) : (
                <Fragment key={isRefresh}>
                  <span>
                    <Trans chiave="Un'unica registrazione," />
                  </span>
                  <span>
                    <Trans chiave="un mondo di opportunità" />
                  </span>
                </Fragment>
              )}
            </h1>
            {isRefresh ? (
              <p className="registrazione-page__description">
                <Trans
                  chiave="Per portare a termine la tua registrazione al sito di Giunti Scuola, inserisci qui
                sotto le informazioni che mancano e che ci aiuteranno a personalizzare la tua
                esperienza"
                />
              </p>
            ) : (
              <div
                className="registrazione-page__description"
                dangerouslySetInnerHTML={{ __html: pagina.descrizione }}
              />
            )}
          </div>
        </header>
        <section className="registrazione__form-section">
          <div className="container">
            {REGISTRATION_DISABLED ? (
              <div className="registrazione-page__form-box">
                <div className="registrazione-page__disabled">
                  <span className="registrazione-page__disabled-badge">Aggiornamento in corso</span>
                  <h2>Registrazioni momentaneamente sospese</h2>
                  <p>
                    Stiamo lavorando per migliorare l’esperienza. Per ora non è possibile creare un
                    nuovo account.
                  </p>
                  {/* <p className="registrazione-page__disabled-note">
                    Hai già un profilo? Puoi accedere o recuperare la password in pochi secondi.
                  </p>
                  <div className="registrazione-page__disabled-actions">
                    <button className="button button--primary" onClick={() => handleLogin(true)}>
                      Accedi
                    </button>
                    <Link legacyBehavior href="/registrazione/recupera-password">
                      <a className="button button--secondary">Recupera la password</a>
                    </Link>
                  </div> */}
                </div>
              </div>
            ) : (
              <>
                <div className="registrazione-page__steps">
                  <div className="steps">
                    <div className={`steps__item ${getClassFromStep(1)}`}>
                      <span className="steps__index">1</span>
                      <span className="steps__label">
                        {typeof window !== 'undefined' && 'exists' in localStorage ? (
                          <Trans chiave={query} chiave="Conferma i tuoi dati" />
                        ) : (
                          <Trans chiave={query} chiave="Inserisci i tuoi dati" />
                        )}
                      </span>
                    </div>
                    <div className={`steps__item ${getClassFromStep(2)}`}>
                      <span className="steps__index">2</span>
                      <span className="steps__label">
                        <Trans chiave="Le tue preferenze" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="registrazione-page__form-box">
                  <Formik
                    initialValues={{
                      ...fieldsStep1,
                      ...fieldsStep2,
                    }}
                    validate={(values) => {
                      try {
                        const schema = getSchema(values)
                        validateYupSchema(values, schema, true, values)
                      } catch (err) {
                        return yupToFormErrors(err) //for rendering validation errors
                      }

                      return {}
                    }}
                    onSubmit={(values, { resetForm }) => {
                      if (step === 2) registraUtente(values)
                    }}
                    enableReinitialize
                  >
                    {(formik) => (
                      <Form className="registrazione-page__form w-form">
                        {step === 1 ? (
                          <Step1 formik={formik} />
                        ) : (
                          <Step2 formik={formik} hidePrivacy={hasFlag} />
                        )}

                        <ReCAPTCHA
                          ref={recaptchaRef}
                          size="invisible"
                          sitekey={RECAPTCHA_KEY}
                          // onChange={onChange}
                        />

                        <div className="registrazione-page__submit-box">
                          <span className="registrazione-page__submit-info w-form__info">
                            L’asterisco (*) indica i campi obbligatori
                          </span>
                          {step === 1 ? (
                            <input
                              type="button"
                              value="Continua"
                              disabled={!(formik.isValid && formik.dirty)}
                              className="registrazione-page__submit-button button button--primary"
                              onClick={() => goToStep2(formik)}
                            />
                          ) : (
                            <input
                              type="submit"
                              value="Registrati"
                              disabled={
                                !formik.dirty ||
                                (formik.isSubmitting && step === 2) ||
                                !formik.isValid
                              }
                              className="registrazione-page__submit-button registrazione-page__submit-button--final button button--primary"
                            />
                          )}
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </>
            )}

            {/* <div className="registrazione-page__other">
              <span className="registrazione-page__other-label">
                <Trans chiave="Oppure" />
              </span>
              <div className="registrazione-page__other-buttons">
                <button className="button button--facebook" role="button">
                  <Icon id="facebook-2" /> <Trans chiave="Continua con Facebook" />
                </button>
                <button className="button button--google" role="button">
                  <Icon id="google" /> <Trans chiave="Continua con Google" />
                </button>
                <button className="button button--zainodigitale" role="button">
                  <LazyLoad height={25} offset={100} once>
                    <img src={zainoDigitale} className="icon" />
                  </LazyLoad>{' '}
                  <Trans chiave="Continua con Zaino Digitale" />
                </button>
              </div>
            </div> */}
          </div>
        </section>
        {!REGISTRATION_DISABLED && step === 1 ? <Vantaggi /> : null}
      </main>
      {emailExists ? (
        <Modal
          disclaimer="L'email inserita risulta essere già registrata, effettua il login o recupera la password."
          isOpen={emailExists}
          close={() => setEmailExists(false)}
        >
          <button className="button button--primary cta" onClick={() => handleLogin(true)}>
            <Trans chiave="Accedi" />
          </button>
          <p className="disclaimer">
            <Trans chiave="Oppure" />{' '}
            <Link legacyBehavior href="/registrazione/recupera-password">
              <a>
                <Trans chiave="Recupera la password" />
              </a>
            </Link>
          </p>
        </Modal>
      ) : null}
      {login && <Login isOpen={login} setLogin={handleLogin} />}
    </>
  )
}

export const getStaticProps = async ({ preview, previewData }) => {
  const headers = preview && previewData?.headers ? { cookie: previewData.headers } : {}

  const pageResponse = await api.get(PAGINEDETAIL, {
    params: {
      chiave: 'registrazione-index',
    },
    headers,
  })
  const pagina = await pageResponse.data

  if (!pagina) {
    return {
      notFound: true,
    }
  }

  const toolbarProps = { toolbarPage: pagina }

  return { props: { pagina, ...toolbarProps, revalidate: 60 * 10 }, revalidate: 60 * 10 }
}

export default Registrazione
